import { useState, useRef, useEffect, FC } from 'react'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { Overlay, Popover } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'

import { graphqlEndpoint } from 'AppSettings'
import { useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import GoBackHeader from 'components/organisms/GoBackHeader'
import DocumentsAddReplaceModal from '../DocumentsAddReplaceModal'
import {
  IdpProcessDocumentsResponse,
  IdpProcessDocumentsVariables,
  IDP_PROCESS_DOCUMENTS
} from 'share/mutations/documents'
import './styles.scss'

const AiDocumentDetail: FC = () => {
  const [show, setShow] = useState(false)
  const [isRejectedDocument, setIsRejectedDocument] = useState(false)
  const [clientPortalDocumentId, setClientPortalDocumentId] =
    useState<number | null>(null)
  const [checkListItemId, setCheckListItemId] = useState<number | null>(null)
  const [showDocReplaceModal, setShowDocReplaceModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [startIdpDocProcessing, setStartIdpDocProcessing] = useState(false)
  const history = useHistory()
  const activeCase = useAppSelector((state) => state.activeCase)
  const documentProcessing = useAppSelector((state) => state.documentProcessing)
  const authentication = useAppSelector((state) => state.authentication)
  const loadingDelay = useLoadingDelay(!documentProcessing)
  const ref = useRef(null)
  const refMore = useRef(null)

  const [idpProcessDocumentsMutation, { loading: idpProcessDocumentsLoading }] =
    useMutation<IdpProcessDocumentsResponse, IdpProcessDocumentsVariables>(
      IDP_PROCESS_DOCUMENTS
    )

  function getCaseId() {
    return activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0
  }

  function showReplaceDoc() {
    if (isRejectedDocument && clientPortalDocumentId) {
      return true
    }
    return false
  }

  useEffect(() => {
    if (documentProcessing) {
      if (documentProcessing.selectedChecklistDocument) {
        if (documentProcessing.selectedChecklistDocument.status) {
          if (
            documentProcessing.selectedChecklistDocument.status.toLowerCase() ==
            'rejected'
          ) {
            setIsRejectedDocument(true)
          }
        }
        if (
          documentProcessing.selectedChecklistDocument.clientPortalDocumentId
        ) {
          setClientPortalDocumentId(
            documentProcessing.selectedChecklistDocument.clientPortalDocumentId
          )
        }
        if (
          documentProcessing.selectedChecklistDocument.matterCheckListItemId
        ) {
          setCheckListItemId(
            documentProcessing.selectedChecklistDocument.matterCheckListItemId
          )
        }
      }
    }
  }, [documentProcessing])

  useEffect(() => {
    if (startIdpDocProcessing) {
      processUploadDocsWithIdp()
    }
  }, [startIdpDocProcessing])

  useEffect(() => {
    if (loadingDelay || idpProcessDocumentsLoading) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [loadingDelay, idpProcessDocumentsLoading])

  const downloadDocument = (
    caseId: number,
    matterDocumentId: number,
    clientPortalDocumentId: number,
    matterCheckListItemId: number,
    filename: string
  ) => {
    const BackendURL = graphqlEndpoint.replace('/graphql', '')
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Authorization', `Bearer ${authentication.idToken}`)
    requestHeaders.set('Access_Token', `${authentication.accessToken}`)

    const options = {
      method: 'GET',
      headers: requestHeaders
    }

    fetch(
      `${BackendURL}api/checklistItemDocument/download/${caseId}/${matterDocumentId}/${clientPortalDocumentId}/${matterCheckListItemId}`,
      options
    )
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = blobUrl
        link.download = filename

        document.body.appendChild(link)

        link.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          })
        )

        document.body.removeChild(link)
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          URL.revokeObjectURL(blobUrl)
        }, 100)
      })
      .catch((err) => console.log(err))
  }

  const handleCloseDocumentsAddReplaceModal = () => {
    setStartIdpDocProcessing(true)
    setShowDocReplaceModal(false)
  }

  async function processUploadDocsWithIdp() {
    const uploadedDocumentIdList: number[] = []
    documentProcessing.documentUploadList.map((d) => {
      uploadedDocumentIdList.push(d.id)
    })

    const input = {
      caseId:
        activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
      uploadedDocumentIdList: uploadedDocumentIdList,
      replaceMatterClientPortalDocumentId: clientPortalDocumentId,
      replaceMatterDocumentCheckListItemId: checkListItemId
    }

    const idpProcessResult = await idpProcessDocumentsMutation({
      variables: { request: input }
    })

    console.log(idpProcessResult.data?.documents.idpProcessDocuments.success)

    setStartIdpDocProcessing(false)
    history.push('/dashboard/documents')
  }

  return (
    <>
      <GoBackHeader
        text={documentProcessing.selectedDocumentChecklistItem?.name ?? ''}
      />
      {isLoading && loadingSpinnerPortal()}
      <div className='ui-docs-detail-container'>
        <div className='ui-doc-details'>
          <div className='ui-docs-item-icon'>
            <span className='mdi mdi-file-pdf' />
          </div>
          <div className='ui-docs-item'>
            <p data-testid='document-detail-selected-doc-name'>
              {documentProcessing.selectedChecklistDocument?.name}
            </p>
            <span
              data-testid='document-detail-selected-doc-uploaded'
              className='ui-doc-upload'
            >
              Uploaded:
              {documentProcessing.selectedChecklistDocument?.uploaded}
            </span>
            {
              <div className='ui-docs-status'>
                <span
                  data-testid='document-detail-selected-doc-status'
                  className={
                    documentProcessing.selectedChecklistDocument?.status ?? ''
                  }
                >
                  {documentProcessing.selectedChecklistDocument?.status}
                </span>
              </div>
            }
          </div>
          <div className='ui-docs-button'>
            <span
              className='mdi mdi-dots-vertical'
              data-testid='document-detail-selected-doc-button'
              onClick={() => setShow(!show)}
              ref={refMore}
            />
            <Overlay
              show={show}
              target={refMore}
              placement='bottom'
              container={ref.current}
              containerPadding={20}
              rootClose={true}
              rootCloseEvent='mousedown'
              onHide={() => setShow(!show)}
            >
              <Popover id='more-options'>
                <Popover.Content style={{ padding: 0 }}>
                  <ul className='ui-doc-options'>
                    <li
                      data-testid='document-detail-selected-doc-download'
                      onClick={() => {
                        if (
                          documentProcessing.selectedChecklistDocument?.uploaded
                        )
                          downloadDocument(
                            getCaseId(),
                            documentProcessing.selectedChecklistDocument
                              ?.matterDocumentId,
                            documentProcessing.selectedChecklistDocument
                              ?.clientPortalDocumentId,
                            documentProcessing.selectedChecklistDocument
                              ?.matterCheckListItemId,
                            documentProcessing.selectedChecklistDocument
                              ?.name ?? ''
                          )
                      }}
                    >
                      Download
                    </li>
                  </ul>
                </Popover.Content>
              </Popover>
            </Overlay>
          </div>
        </div>

        <div className='ui-replace-button'>
          {showReplaceDoc() && (
            <Button
              data-testid='document-ai-detail-replace-document-button'
              variant='primary'
              onClick={() => setShowDocReplaceModal(true)}
            >
              Replace Document
            </Button>
          )}
        </div>

        <div className='ui-doc-comments'>
          <div className='ui-doc-comments-text'>Document Comment(s)</div>
          {documentProcessing.selectedChecklistDocument?.comments}
        </div>
      </div>

      {showReplaceDoc() && (
        <DocumentsAddReplaceModal
          title='Replace Document'
          show={showDocReplaceModal}
          onHide={handleCloseDocumentsAddReplaceModal}
          replacedDocumentId={null}
          businessObjectId={null}
          businessObjectType={'ignorechangetracking'}
          folderName='Important: Upload all images for a document at the same time to ensure proper processing.'
          folderDescription=''
          isAiCase={true}
        />
      )}
    </>
  )
}

export default AiDocumentDetail
